import { useState, FormEvent } from "react";
import Navbar from "../home/components/Navbar";
import Footer from "../home/components/Footer";
import SeoMeta from "../../components/base/SeoMeta";

const BASE_URL = (import.meta.env.VITE_SITE_URL as string) || "https://flightfare.pk";

const INSURANCE_JSON_LD = [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Travel Insurance Pakistan – flightfare.pk",
    "url": `${BASE_URL}/insurance`,
    "description": "Get best prices on travel insurance in Pakistan with flightfare.pk. Comprehensive plans covering medical emergencies for domestic and international travel."
  },
  {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Travel Insurance Pakistan",
    "serviceType": "Travel Insurance",
    "provider": {
      "@type": "TravelAgency",
      "name": "flightfare.pk",
      "url": BASE_URL,
      "telephone": "+923711432451"
    },
    "areaServed": "PK",
    "description": "Comprehensive travel insurance plans for Pakistani travelers. Coverage for medical emergencies, trip cancellation, lost baggage and more. Flexible plans for short and long trips.",
    "offers": {
      "@type": "AggregateOffer",
      "priceCurrency": "PKR",
      "availability": "https://schema.org/InStock"
    }
  }
];

const DURATIONS = [
  "1 Week", "2 Weeks", "3 Weeks", "1 Month", "2 Months", "3 Months", "6 Months", "1 Year",
];

export default function InsurancePage() {
  const [country, setCountry] = useState("");
  const [duration, setDuration] = useState("1 Week");
  const [startDate, setStartDate] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!country.trim()) { setError("Please enter a destination country."); return; }
    if (!startDate) { setError("Please select a starting date."); return; }
    setError("");
    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      body.append("country", country);
      body.append("duration", duration);
      body.append("startDate", startDate);
      const res = await fetch("https://readdy.ai/api/form/d7240ehlum2co2q1g1qg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (res.ok) { setSubmitted(true); } else { setError("Something went wrong. Please try again."); }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <SeoMeta
        title="Travel Insurance Pakistan – Best Prices for International & Domestic Travel | flightfare.pk"
        description="Get the best travel insurance prices in Pakistan with flightfare.pk. Comprehensive coverage for medical emergencies, trip cancellation and lost baggage. Flexible plans for all destinations."
        keywords="travel insurance Pakistan, travel insurance Lahore, international travel insurance Pakistan, cheap travel insurance Pakistan, Schengen travel insurance Pakistan"
        canonical="/insurance"
        jsonLd={INSURANCE_JSON_LD}
      />

      {/* Hero Banner */}
      <div className="pt-16">
        <div className="relative overflow-hidden" style={{ background: "linear-gradient(135deg, #0891b2 0%, #06b6d4 40%, #22d3ee 70%, #a3e8ef 100%)" }}>
          <div className="absolute top-0 left-0 right-0 h-2 bg-amber-400" />
          <div className="absolute top-6 right-24 w-10 h-10 rounded-full border-2 border-white/30" />
          <div className="absolute top-16 right-64 w-5 h-5 rounded-full border border-white/20" />
          <div className="absolute bottom-8 left-16 w-8 h-8 rounded-full border border-white/20" />
          <div className="absolute bottom-16 left-48 w-4 h-4 rounded-full border border-white/30" />

          <div className="max-w-7xl mx-auto px-6 py-16 flex items-center justify-between">
            <div className="max-w-xl">
              <h1 className="text-3xl font-extrabold text-white leading-tight tracking-wide uppercase text-center w-full">
                Book a Travel Insurance
              </h1>
              <p className="text-white/90 text-sm mt-3 leading-relaxed text-center">
                Get best prices on travel insurance bookings
              </p>
            </div>
            <div className="flex-shrink-0">
              <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-2xl">
                <i className="ri-shield-check-line text-5xl text-white"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search Form */}
      <div className="max-w-7xl mx-auto px-6 -mt-6 relative z-10 mb-10">
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
          {/* Tabs */}
          <div className="flex gap-1 mb-6">
            <a href="/" className="px-5 py-2 rounded-md text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer">
              Flights
            </a>
            <span className="px-5 py-2 rounded-md text-sm font-semibold text-white bg-cyan-600 whitespace-nowrap">
              Insurance
            </span>
          </div>

          {submitted ? (
            <div className="text-center py-8">
              <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3 bg-green-100 rounded-full">
                <i className="ri-check-line text-3xl text-green-600"></i>
              </div>
              <p className="text-gray-900 font-semibold text-lg">Inquiry Submitted!</p>
              <p className="text-gray-500 text-sm mt-1">Our team will get back to you with the best insurance prices shortly.</p>
              <button type="button" onClick={() => { setSubmitted(false); setCountry(""); setStartDate(""); setDuration("1 Week"); }} className="mt-5 px-6 py-2 bg-cyan-600 text-white text-sm font-semibold rounded-md hover:bg-cyan-700 transition-colors cursor-pointer whitespace-nowrap">
                New Inquiry
              </button>
            </div>
          ) : (
            <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col md:flex-row items-stretch md:items-end gap-4">
              <div className="flex-1">
                <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Country</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none w-5 h-full">
                    <i className="ri-map-pin-line text-gray-400 text-sm"></i>
                  </div>
                  <input
                    type="text"
                    name="country"
                    value={country}
                    onChange={e => setCountry(e.target.value)}
                    placeholder="Type few characters..."
                    className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-cyan-400 transition-colors"
                  />
                </div>
              </div>
              <div className="md:w-44">
                <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Duration</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none w-5 h-full">
                    <i className="ri-time-line text-gray-400 text-sm"></i>
                  </div>
                  <select
                    name="duration"
                    value={duration}
                    onChange={e => setDuration(e.target.value)}
                    className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-cyan-400 transition-colors appearance-none bg-white cursor-pointer"
                  >
                    {DURATIONS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              </div>
              <div className="md:w-48">
                <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Starting Date</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none w-5 h-full">
                    <i className="ri-calendar-line text-gray-400 text-sm"></i>
                  </div>
                  <input
                    type="date"
                    name="startDate"
                    value={startDate}
                    onChange={e => setStartDate(e.target.value)}
                    className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-cyan-400 transition-colors cursor-pointer"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2.5 bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold rounded-md transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 flex items-center justify-center gap-2"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-search-line text-sm"></i>
                </div>
                {submitting ? "Submitting..." : "GET PRICES"}
              </button>
            </form>
          )}
          {error && <p className="mt-3 text-red-500 text-xs">{error}</p>}
        </div>
      </div>

      {/* Description */}
      <section className="py-10 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6 space-y-4">
          <p className="text-sm text-gray-600 leading-relaxed">
            Ensure your journey is worry-free with travel insurance from flightfare.pk. Our comprehensive plans protect you against a wide range of unexpected events, including medical emergencies. With our expert guidance, tailor your insurance plan to match your unique needs, providing peace of mind throughout your travels.
          </p>
          <p className="text-sm text-gray-600 leading-relaxed">
            At flightfare.pk, we understand that every traveller is different. That&apos;s why we offer flexible insurance options suitable for various trip types, whether it&apos;s a brief domestic escape or an extended international adventure. Through our partnerships with leading insurance providers in Pakistan, we guarantee competitive rates and top-notch coverage.
          </p>
          <p className="text-sm text-gray-600 leading-relaxed">
            Booking your travel insurance is effortless with flightfare.pk&apos;s streamlined process. Our dedicated team is on hand to assist with any questions and provide detailed insights into each plan&apos;s benefits and coverage. Protect your travel investment with confidence — book your travel insurance with flightfare.pk today and embark on your journey knowing you&apos;re fully protected against unforeseen events.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
